import { useEffect, useState } from "react";
import {
  Modal,
  Select,
  Textarea,
  TextInput,
  Button,
  Group,
  Stack,
  Text,
  Alert,
} from "@mantine/core";
import { useQuery } from "@tanstack/react-query";
import { tripsApi } from "../../api/trips";
import { humanize } from "../../lib/labels";

const ITEM_TYPES = [
  "electronics",
  "documents",
  "clothingAndAccessories",
  "luggage",
  "other",
];

const isObjectId = (s: string) => /^[0-9a-fA-F]{24}$/.test(s.trim());

export interface LostItemCreateBody {
  itemType: string;
  message: string;
  tripID: string;
  userID: string;
  driverID: string;
}

export function LostItemFormModal({
  opened,
  onClose,
  loading,
  onSubmit,
  presetTripID,
}: {
  opened: boolean;
  onClose: () => void;
  loading: boolean;
  onSubmit: (body: LostItemCreateBody) => void;
  presetTripID?: string;
}) {
  const [tripID, setTripID] = useState("");
  const [itemType, setItemType] = useState("other");
  const [message, setMessage] = useState("");

  useEffect(() => {
    if (opened) {
      setTripID(presetTripID ?? "");
      setItemType("other");
      setMessage("");
    }
  }, [opened, presetTripID]);

  // Look up the trip to derive userID + driverID (both required by the API).
  const trip = useQuery({
    queryKey: ["trip-lookup", tripID],
    queryFn: () => tripsApi.get(tripID.trim()),
    enabled: opened && isObjectId(tripID),
  });

  const userID = trip.data?.passengerDetail?.passengerID ?? "";
  const driverID = trip.data?.driverDetail?.driverID ?? "";
  const valid = message.trim() && isObjectId(tripID) && userID && driverID;

  return (
    <Modal opened={opened} onClose={onClose} title="New lost item" centered size="lg">
      <Stack>
        <TextInput
          label="Trip ID"
          placeholder="24-character trip id"
          value={tripID}
          onChange={(e) => setTripID(e.currentTarget.value)}
          required
          error={
            tripID && !isObjectId(tripID) ? "Must be a 24-character id" : undefined
          }
        />
        {isObjectId(tripID) && (
          <>
            {trip.isLoading && (
              <Text size="sm" c="dimmed">
                Looking up trip…
              </Text>
            )}
            {trip.isError && (
              <Alert color="red">Trip not found.</Alert>
            )}
            {trip.data && (
              <Alert color={userID && driverID ? "green" : "yellow"}>
                Rider: {trip.data.passengerDetail?.name ?? "—"} · Driver:{" "}
                {trip.data.driverDetail?.name ?? "—"}
                {(!userID || !driverID) &&
                  " — this trip has no rider/driver linked, can't attach a lost item."}
              </Alert>
            )}
          </>
        )}
        <Select
          label="Item type"
          data={ITEM_TYPES.map((t) => ({ value: t, label: humanize(t) }))}
          value={itemType}
          onChange={(v) => setItemType(v ?? "other")}
          allowDeselect={false}
        />
        <Textarea
          label="Description"
          value={message}
          onChange={(e) => setMessage(e.currentTarget.value)}
          minRows={3}
          autosize
          required
        />
        <Group justify="flex-end" mt="sm">
          <Button variant="default" onClick={onClose}>
            Cancel
          </Button>
          <Button
            loading={loading}
            disabled={!valid}
            onClick={() =>
              onSubmit({
                itemType,
                message: message.trim(),
                tripID: tripID.trim(),
                userID,
                driverID,
              })
            }
          >
            Create lost item
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
